<script setup lang="ts">
import ReportTemplateMenu from '@/components/createdUi/ReportTemplateMenu.vue'
import { improvementReportFilters } from '@/modules/improvements/utils/reportFilters'

/*
* Компонент RegistrPPU - Таблица реестра предложений по улучшению
*/

import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { useDisplay, useTheme } from 'vuetify'
import { VIconBtn } from 'vuetify/labs/VIconBtn'


import CollapsibleText from '@/components/createdUi/CollapsibleText.vue'
import ShortFio from '@/components/createdUi/ShortFio.vue'
import DateRangeFilterCard from '@/components/createdUi/DateRangeFilterCard.vue'
import UniversalFilterTree from '@/components/createdUi/UniversalFilterTree.vue'
import { type IFilterConfig, useTableFilters } from '@/composables/useTableFilters'
import { useTableItemsPerPage } from '@/composables/useTableItemsPerPage'
import ImprovementFormModal from '@/modules/improvements/components/ImprovementFormModal.vue'
import { useImprovementsStore } from '@/modules/improvements/stores/improvementsStore'
import type { IImprovementSuggestion } from '@/modules/improvements/types/IImprovementSuggestion'
import OnboardingLaunchButton from '@/modules/onboarding/components/OnboardingLaunchButton.vue'
import { useOnboardingContext } from '@/modules/onboarding/composables/useOnboardingContext'
import { useOnboardingDemoRows } from '@/modules/onboarding/composables/useOnboardingDemoRows'
import { useOnboardingStore } from '@/modules/onboarding/stores/onboardingStore'
import { IMPROVEMENTS_ANCHORS, IMPROVEMENTS_CONTEXTS } from '@/modules/onboarding/scenarios/improvements/anchors'
import { IMPROVEMENTS_FORM_DEMO_INDEX } from '@/modules/onboarding/scenarios/improvements/adapter'
import {
  getPpuAggregatedStatusColor,
  getPpuAggregatedStatusLabel,
  getPpuStatusColor,
  getPpuStatusLabel,
  PPU_AGGREGATED_STATUS_MAP,
  PPU_STATUS_MAP,
} from '@/modules/improvements/utils/ppuStatusHelper'
import { formatDateOnly } from '@/utils/formatters'

const theme = useTheme()
const { smAndDown } = useDisplay()
const improvementsStore = useImprovementsStore()

const { itemsPerPage } = useTableItemsPerPage('ppuRegistry', 25)
const { items, totalCount, currentSortString, nextCursor } = storeToRefs(improvementsStore)

improvementsStore.resetFilters()

const selectedSuggestionId = ref<number | null>(null)
const isDetailModalOpen = ref(false)

const filterConfigs: IFilterConfig[] = [
  { key: 'suggestion_types', labelPrefix: 'Вид ППУ: ', type: 'tree' },
  { key: 'creators', labelPrefix: 'Автор: ', type: 'tree' },
  { key: 'coordinators', labelPrefix: 'Председатель: ', type: 'tree' },
  { key: 'status', labelPrefix: 'Статус: ', type: 'status', getCustomLabel: (val: string) => PPU_AGGREGATED_STATUS_MAP[val]?.label || val },
  { key: 'stages', labelPrefix: 'Стадия: ', type: 'status', getCustomLabel: (val: string) => PPU_STATUS_MAP[val]?.label || val },
  { key: 'date_time', labelPrefix: 'Дата подачи: ', type: 'date_range', rangeStartKey: 'created_after', rangeEndKey: 'created_before' },
  { key: 'decision_date', labelPrefix: 'Дата решения: ', type: 'date_range', rangeStartKey: 'decision_after', rangeEndKey: 'decision_before' },
  { key: 'requires_action', labelPrefix: 'Требует действия: ', type: 'boolean' },
]

const tableLogic = useTableFilters({
  filters: computed(() => improvementsStore.filters),
  searchChoices: computed(() => improvementsStore.searchChoices) as any,
  filterChoices: computed(() => improvementsStore.filterChoices) as any,
  filterLoading: improvementsStore.filterLoading,
  fetchFilterChoices: (key: string) => improvementsStore.fetchFilterChoices(key),
  fetchData: (cursor?: string, limit?: number, throwOnError?: boolean) => improvementsStore.fetchSuggestions(cursor, limit, throwOnError),
  realtimeEnabled: true,
  currentSortString,
  itemsPerPage,
  resetFilters: () => improvementsStore.resetFilters(),
  nextCursor,
  loadedItemsCount: computed(() => improvementsStore.items.length),
  totalItemsCount: computed(() => improvementsStore.totalCount),
  sortMap: {},
  filterConfigs,
})

const parseDateFromInput = (dateString: string): Date | null => {
  if (!dateString) return null
  const [day, month, year] = dateString.split('.').map(Number)
  if (!day || !month || !year) return null
  return new Date(year, month - 1, day)
}

const applyCreatedDateFilter = () => {
  improvementsStore.filters.created_after = parseDateFromInput(tableLogic.tempValues.value.created_after || '')
  improvementsStore.filters.created_before = parseDateFromInput(tableLogic.tempValues.value.created_before || '')
  tableLogic.triggerServerFilter()
  tableLogic.filterMenus.value.date_time = false
}

const applyDecisionDateFilter = () => {
  improvementsStore.filters.decision_after = parseDateFromInput(tableLogic.tempValues.value.decision_after || '')
  improvementsStore.filters.decision_before = parseDateFromInput(tableLogic.tempValues.value.decision_before || '')
  tableLogic.triggerServerFilter()
  tableLogic.filterMenus.value.decision_date = false
}

const headers = computed(() => [
  { title: '№', key: 'id', sortable: false },
  { title: 'Предложение', key: 'title', sortable: false },
  { title: 'Описание', key: 'text', sortable: false },
  { title: 'Вид ППУ', key: 'suggestion_type', sortable: false, filterType: 'tree', filterKey: 'suggestion_types' },
  { title: 'Автор', key: 'created_by', sortable: false, filterType: 'tree', filterKey: 'creators' },
  { title: 'Председатель', key: 'coordinator', sortable: false, filterType: 'tree', filterKey: 'coordinators' },
  { title: 'Дата подачи', key: 'created_at', sortable: false, filterType: 'date_range', filterKey: 'date_time' },
  { title: 'Дата решения', key: 'decision_date', sortable: false, filterType: 'date_range', filterKey: 'decision_date' },
  { title: 'Статус', key: 'status', sortable: false, filterType: 'tree', filterKey: 'status' },
  { title: 'Стадия', key: 'stage', sortable: false, filterType: 'tree', filterKey: 'stages' },
] as any[])

const displayedItems = computed(() => {
  const start = (tableLogic.currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return items.value.slice(start, end)
})

const openSuggestionCard = (id: number) => {
  selectedSuggestionId.value = id
  isDetailModalOpen.value = true
}

function onRowClick(_event: unknown, row: { item: IImprovementSuggestion }) {
  openSuggestionCard(row.item.id)
}

const isRequiresActionFilterActive = computed(() => Boolean(improvementsStore.filters.requires_action))

const toggleRequiresActionFilter = () => {
  improvementsStore.filters.requires_action = isRequiresActionFilterActive.value ? undefined : true
  tableLogic.triggerServerFilter()
}

/* Онбординг реестра: демо-предложения первыми в таблице, учебная карточка открывается штатной модалкой */
const onboardingStore = useOnboardingStore()
const { rows: tableRows } = useOnboardingDemoRows('improvement', displayedItems)
const onboardingFormDemoId = computed(() => onboardingStore.activeDemoItems[IMPROVEMENTS_FORM_DEMO_INDEX]?.list.id ?? null)

useOnboardingContext({
  names: [IMPROVEMENTS_CONTEXTS.registry],
  scenarioId: 'improvements.registry',
  root: () => document.body,
  actions: {
    openDemoForm: () => {
      if (onboardingFormDemoId.value !== null)
        openSuggestionCard(onboardingFormDemoId.value)
    },
    closeForm: () => {
      isDetailModalOpen.value = false
      selectedSuggestionId.value = null
    },
  },
})

const rowProps = ({ item }: { item: IImprovementSuggestion }) => {
  return {
    class: {
      'row--requires-action': item.requires_action === true,
    },
    'data-onboarding': item.id === onboardingFormDemoId.value ? IMPROVEMENTS_ANCHORS.registry.demoRow : undefined,
  }
}

onMounted(() => {
  tableLogic.triggerServerFilter()
})
const templateFilters = computed(() => improvementReportFilters(improvementsStore.filters))
</script>

<template>
  <div class="w-100 d-flex flex-column gap-4">
    <VCard class="border overflow-hidden" elevation="0">
      <div
        class="d-flex align-center gap-4"
        :class="[
          tableLogic.activeFilterChips.value.length ? 'px-4 pt-4' : 'pa-4',
          { 'flex-column': smAndDown },
        ]"
      >
        <div
          class="d-flex align-center gap-2 w-100"
          :class="{ 'flex-column': smAndDown }"
        >
          <VTextField
            v-model="tableLogic.searchFilter.value"
            class="flex-grow-0"
            :class="smAndDown ? 'w-100' : 'w-25'"
            variant="outlined"
            clearable
            placeholder="Фильтр по ключевому слову"
            :data-onboarding="IMPROVEMENTS_ANCHORS.registry.search"
            autocomplete="off"
            @keyup.enter="tableLogic.applySearchFilter()"
          />

          <VIconBtn
            v-if="tableLogic.searchFilter.value"
            :class="{ 'w-100': smAndDown }"
            color="primary"
            class="rounded-sm"
            icon="ri-filter-2-line"
            variant="outlined"
            :rounded="false"
            width="48"
            height="48"
            @click="tableLogic.applySearchFilter()"
          />

          <VBtn
            :class="[
              { 'w-100': smAndDown },
              !smAndDown && 'ml-auto',
            ]"
            :prepend-icon="tableLogic.getFilterIcon('requires_action')"
            variant="outlined"
            color="secondary"
            height="48"
            :loading="improvementsStore.isLoading"
            :data-onboarding="IMPROVEMENTS_ANCHORS.registry.requiresAction"
            @click="toggleRequiresActionFilter"
          >
            Требует действия
          </VBtn>

          <div class="d-flex align-center gap-2">
            <ReportTemplateMenu object-type="improvement" context="collection" :filters="templateFilters" :sort="improvementsStore.currentSortString" variant="toolbar" :size="48" />
            <OnboardingLaunchButton scenario="improvements.registry" :size="48" />
          </div>

          <VBtn
            v-if="tableLogic.hasActiveFilters.value"
            :class="{ 'w-100': smAndDown }"
            prepend-icon="ri-delete-bin-line"
            variant="outlined"
            color="secondary"
            height="48"
            :loading="improvementsStore.isLoading"
            :data-onboarding="IMPROVEMENTS_ANCHORS.registry.resetFilters"
            @click="tableLogic.renewDataAndResetFilters()"
          >
            Сбросить фильтры
          </VBtn>
        </div>
      </div>

      <div
        v-if="tableLogic.activeFilterChips.value.length"
        class="d-flex flex-wrap align-center gap-2 px-4 py-3"
      >
        <VChip
          v-for="chip in tableLogic.activeFilterChips.value"
          :key="chip.key || chip.id || `${chip.config?.key || chip.type}-${chip.value ?? 'no-value'}`"
          closable
          @click:close="tableLogic.removeFilter(chip)"
        >
          {{ chip.label }}
        </VChip>
      </div>

      <VDataTableServer
        :headers="headers"
        :items-per-page="itemsPerPage"
        :items="tableRows"
        :items-length="totalCount"
        :loading="improvementsStore.isLoading"
        :row-props="rowProps"
        class="no-row-gaps cursor-pointer-table custom-data-table"
        :class="[{ 'dark-theme': theme.global.current.value.dark }]"
        @update:options="tableLogic.handlePageChange"
        @click:row="onRowClick"
      >
        <template #no-data>
          <div class="text-center text-italic py-4 text-medium-emphasis">
            Предложения по улучшению не найдены
          </div>
        </template>

        <template
          v-for="col in headers.filter(h => h.filterType)"
          :key="col.key"
          #[`header.${col.key}`]="{ column }"
        >
          <div class="d-flex align-center" :data-onboarding="IMPROVEMENTS_ANCHORS.registry.filterPrefix + col.key">
            <span>{{ column.title }}</span>

            <div
              v-if="tableLogic.filterLoading?.[col.filterKey!]"
              class="d-flex align-center justify-center ml-1"
              style="width: 28px; height: 28px"
            >
              <VProgressCircular
                indeterminate
                color="primary"
                size="20"
                width="2"
              />
            </div>

            <VIconBtn
              v-else
              variant="plain"
              size="small"
              :icon="tableLogic.getFilterIcon(col.filterKey!)"
              @click.stop="tableLogic.handleFilterClick(col.filterKey!)"
            />

            <VMenu
              v-if="col.filterType === 'date_range' && col.filterKey === 'date_time'"
              v-model="tableLogic.filterMenus.value[col.filterKey]"
              :close-on-content-click="false"
              location="bottom"
              transition="scale-transition"
              :max-width="220"
            >
              <template #activator="{ props: menuProps }">
                <div v-bind="menuProps" />
              </template>
              <DateRangeFilterCard
                :date-after="tableLogic.tempValues.value.created_after || ''"
                :date-before="tableLogic.tempValues.value.created_before || ''"
                @update:date-after="tableLogic.tempValues.value.created_after = $event"
                @update:date-before="tableLogic.tempValues.value.created_before = $event"
                @apply="applyCreatedDateFilter"
              />
            </VMenu>

            <VMenu
              v-else-if="col.filterType === 'date_range' && col.filterKey === 'decision_date'"
              v-model="tableLogic.filterMenus.value[col.filterKey]"
              :close-on-content-click="false"
              location="bottom"
              transition="scale-transition"
              :max-width="220"
            >
              <template #activator="{ props: menuProps }">
                <div v-bind="menuProps" />
              </template>
              <DateRangeFilterCard
                :date-after="tableLogic.tempValues.value.decision_after || ''"
                :date-before="tableLogic.tempValues.value.decision_before || ''"
                @update:date-after="tableLogic.tempValues.value.decision_after = $event"
                @update:date-before="tableLogic.tempValues.value.decision_before = $event"
                @apply="applyDecisionDateFilter"
              />
            </VMenu>

            <VMenu
              v-else
              v-model="tableLogic.filterMenus.value[col.filterKey!]"
              :close-on-content-click="false"
              location="bottom start"
              transition="scale-transition"
            >
              <template #activator="{ props: menuProps }">
                <div v-bind="menuProps" />
              </template>
              <UniversalFilterTree
                :field-key="col.filterKey!"
                :store="improvementsStore"
                :model-value="tableLogic.tempSelectedFilters.value[col.filterKey!] || []"
                :placeholder="`Поиск: ${column.title}`"
                @update:model-value="tableLogic.tempSelectedFilters.value[col.filterKey!] = $event"
                @apply="tableLogic.applyFilter(col.filterKey!)"
              />
            </VMenu>
          </div>
        </template>

        <template #[`item.id`]="{ item }">
          <span>{{ item.id }}</span>
        </template>

        <template #[`item.title`]="{ item }">
          <span>{{ item.title }}</span>
        </template>

        <template #[`item.text`]="{ item }">
          <CollapsibleText :text="item.text" />
        </template>

        <template #[`item.suggestion_type`]="{ item }">
          <span>{{ item.suggestion_type?.short_name || item.suggestion_type?.name || '' }}</span>
        </template>

        <template #[`item.created_by`]="{ item }">
          <ShortFio :person="item.created_by" />
        </template>

        <template #[`item.coordinator`]="{ item }">
          <ShortFio :person="item.coordinator" />
        </template>

        <template #[`item.created_at`]="{ item }">
          <span>{{ formatDateOnly(item.created_at) }}</span>
        </template>

        <template #[`item.decision_date`]="{ item }">
          <span>{{ formatDateOnly(item.decision_date) }}</span>
        </template>

        <template #[`item.status`]="{ item }">
          <VChip
            size="small"
            :color="getPpuAggregatedStatusColor(item.status)"
            variant="tonal"
          >
            {{ getPpuAggregatedStatusLabel(item.status, item.status_display) }}
          </VChip>
        </template>

        <template #[`item.stage`]="{ item }">
          <span
            class="body-2 font-weight-medium"
            :class="`text-${getPpuStatusColor(item.stage || item.workflow_status)}`"
          >
            {{ getPpuStatusLabel(item.stage || item.workflow_status, item.stage_display || item.workflow_status_display) }}
          </span>
        </template>

        <template #bottom>
          <div class="custom-data-table-footer">
            <VDataTableFooter
              :show-current-page="true"
              :items-per-page-options="[10, 20, 50, 100]"
            />
          </div>
        </template>
      </VDataTableServer>
    </VCard>

    <ImprovementFormModal
      v-model="isDetailModalOpen"
      :suggestion-id="selectedSuggestionId"
    />
  </div>
</template>

<style scoped lang="scss">
:deep(tbody .v-data-table__tr .v-data-table__td:first-child) {
  color: rgb(var(--v-theme-primary-500)) !important;
}

:deep(.custom-data-table .v-table__wrapper) {
  overflow-x: auto;
}

:deep(.custom-data-table table) {
  table-layout: fixed;
  min-inline-size: 105.75rem;
}

:deep(.custom-data-table .v-data-table__th) {
  background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  line-height: 22px !important;
  position: relative;
  padding-inline: 1rem;
  white-space: nowrap;
}

:deep(.custom-data-table .v-data-table__td) {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  padding-inline: 1rem;
  white-space: normal;
  word-wrap: break-word;
}

:deep(.custom-data-table .v-data-table__th:not(:last-child))::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 2px;
  height: 14px;
  background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
}

/* № — аудиты (без иконки фильтра, несколько цифр) */
:deep(.custom-data-table th:nth-child(1)),
:deep(.custom-data-table td:nth-child(1)) {
  width: 3.5rem !important;
  min-width: 3.5rem !important;
}

/* Предложение — аудиты «Название» */
:deep(.custom-data-table th:nth-child(2)),
:deep(.custom-data-table td:nth-child(2)) {
  width: 14rem !important;
  min-width: 14rem !important;
}

/* Описание — аудиты «Норматив» (длинный текст) */
:deep(.custom-data-table th:nth-child(3)),
:deep(.custom-data-table td:nth-child(3)) {
  width: 22rem !important;
  min-width: 22rem !important;
}

/* Вид ППУ — аудиты «Вид» */
:deep(.custom-data-table th:nth-child(4)),
:deep(.custom-data-table td:nth-child(4)) {
  width: 8rem !important;
  min-width: 8rem !important;
}

/* Автор — НС / заявки «Регистратор» */
:deep(.custom-data-table th:nth-child(5)),
:deep(.custom-data-table td:nth-child(5)) {
  width: 10rem !important;
  min-width: 10rem !important;
}

/* Председатель — НС «Координатор» */
:deep(.custom-data-table th:nth-child(6)),
:deep(.custom-data-table td:nth-child(6)) {
  width: 11.25rem !important;
  min-width: 11.25rem !important;
}

/* Дата подачи / Дата решения — аудиты «Дата нач.» / «Дата кон.» */
:deep(.custom-data-table th:nth-child(7)),
:deep(.custom-data-table td:nth-child(7)),
:deep(.custom-data-table th:nth-child(8)),
:deep(.custom-data-table td:nth-child(8)) {
  width: 10rem !important;
  min-width: 10rem !important;
}

/* Статус — НС / заявки */
:deep(.custom-data-table th:nth-child(9)),
:deep(.custom-data-table td:nth-child(9)) {
  width: 8rem !important;
  min-width: 8rem !important;
}

/* Стадия — аудиты «Статус» (ближайший аналог) */
:deep(.custom-data-table th:nth-child(10)),
:deep(.custom-data-table td:nth-child(10)) {
  width: 9rem !important;
  min-width: 9rem !important;
}

:deep(.custom-data-table tr.row--requires-action > .v-data-table__td:first-child) {
  border-inline-start: 4px solid rgb(var(--v-theme-error)) !important;
}

:deep(.custom-data-table tr):hover {
  box-shadow: 0 2px 4px rgb(var(--v-shadow-key-umbra-color), var(--v-shadow-xs-opacity));
  cursor: pointer;
  background-color: var(--table-row-hover) !important;
}

:deep(.custom-data-table.dark-theme tr:hover) {
  box-shadow: 0 2px 4px rgba(99, 118, 135, 0.2) !important;
}

:deep(.v-data-table-footer) {
  display: flex;
  justify-content: center;
  align-items: center;
}

.custom-data-table-footer {
  :deep(.v-data-table-footer) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;

    .v-data-table-footer__items-per-page > span:first-child {
      display: none !important;
    }

    .v-data-table-footer__pagination {
      order: 1;
    }

    .v-data-table-footer__items-per-page {
      order: 2;
    }

    .v-data-table-footer__info {
      order: 3;
    }

    .v-pagination__last > button {
      display: none !important;
    }

    .v-pagination__next > button[aria-disabled="true"] {
      display: none !important;
    }
  }
}
</style>
